import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Scale, ShieldCheck, Tag, MessagesSquare, Loader2, AlertTriangle, BadgeCheck, Mic, Keyboard } from 'lucide-react';
import { languoidEnglishName } from '@kolectra/shared/languoids';
import { Connectedness, Preparedness, SocialDifferential, decodeRegister } from '@kolectra/shared/registers';
import { ChallengeState, type ChallengeStateName } from '@kolectra/shared/states';
import { Permission } from '@kolectra/shared/permissions';
import { FAILURE_LABELS, failureLabel } from '@kolectra/shared/taxonomy';
import type { ChallengeReview, ReviewTask, ReviewTake } from '@kolectra/shared/schemas';
import { CardSwitch } from '@kolectra/ui';
import { useLanguage } from '../language.js';
import { useWebAuth, useWebPerms } from '../auth.js';
import { challengesApi, ApiError, newIdempotencyKey } from '../lib/api-client.js';
import { PageHead } from '../components/page-head.js';

type Verdict = 'PASS' | 'FAIL' | 'OTHER';
type Route = 'CHALLENGE_SET' | 'NON_CHALLENGE_SET';

// 2nd-review + disputed are retired in the UI — the queue is just first-verdict
// (TESTED) then completion (JUDGED).
const TO_JUDGE: ChallengeStateName[] = [
  ChallengeState.TESTED,
  ChallengeState.JUDGED,
];

const STATE_LABEL: Partial<Record<ChallengeStateName, string>> = {
  TESTED: 'To judge',
  JUDGED: 'To complete',
};

/**
 * Judgement (web, live). The reviewer compares the model's output to the gold
 * reference for each task and records a PASS/FAIL verdict. A FAIL on either task
 * means the model was defeated → the reviewer tags the Kolectra taxonomy label that
 * explains the failure, and the segment ships as a CHALLENGE SET. Data comes
 * from `GET /challenges/:id/review`; verdicts go to `POST .../judge` (or
 * `.../tiebreak` for a disputed challenge).
 */
export function JudgementPage() {
  const { account } = useWebAuth();
  const teamId = account.team.id;
  const { langs, lang, setLang } = useLanguage();
  const [selectedId, setSelectedId] = useState<string | undefined>();
  // Once the reviewer explicitly picks a language, stop auto-landing on the busiest.
  const [pickedLang, setPickedLang] = useState(false);

  // Fetch the full to-judge set server-side (all pages), not just the first page —
  // otherwise a team with more challenges than one page holds under-shows the
  // queue versus the dashboard's count.
  // FORWARD only: reverse challenges are judged from the team's back-translation
  // via the sheet round-trip, not this PASS/FAIL-against-English-gold UI — so they
  // must never enter the human judge's queue.
  const queueQuery = useQuery({
    queryKey: ['challenges', teamId, 'to-judge'],
    queryFn: () => challengesApi.listAll(teamId, TO_JUDGE, 'FORWARD'),
  });

  const allToJudge = queueQuery.data ?? [];

  // Per-language pending split so the switcher shows where the work is — no more
  // clicking through languages to find one with a queue.
  const pendingFor = (c: string) => {
    const items = allToJudge.filter((q) => q.languoidCode === c);
    const toJudge = items.filter((q) => q.state === ChallengeState.TESTED).length;
    return { total: items.length, toJudge, toComplete: items.length - toJudge };
  };
  const langsByWork = [...langs].sort((a, b) => pendingFor(b).total - pendingFor(a).total);
  // Land on the busiest language with work unless the reviewer explicitly picked one.
  const effLang = pickedLang || pendingFor(lang).total > 0 ? lang : langsByWork[0] ?? lang;
  const pickLang = (c: string) => { setPickedLang(true); setLang(c); };

  const langQueue = allToJudge.filter((c) => c.languoidCode === effLang);
  const langQueueKey = langQueue.map((c) => c.id).join(',');

  // Queue split: still needs a first verdict (TESTED) vs. judged and waiting to be completed.
  const awaitingVerdict = langQueue.filter((c) => c.state === ChallengeState.TESTED).length;
  const toComplete = langQueue.length - awaitingVerdict;

  // Keep a valid selection: first reviewable in the current language.
  useEffect(() => {
    setSelectedId((cur) => (cur && langQueue.some((c) => c.id === cur) ? cur : langQueue[0]?.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langQueueKey]);

  const selectedIndex = langQueue.findIndex((c) => c.id === selectedId);

  // Move through the queue by offset — powers j/k navigation and the auto-advance
  // after a verdict is recorded. Never wraps: staying put on the last item is a
  // clearer signal than looping back to the top.
  const step = (delta: number) => {
    setSelectedId((cur) => {
      const idx = langQueue.findIndex((c) => c.id === cur);
      if (idx === -1) return langQueue[0]?.id ?? cur;
      return langQueue[idx + delta]?.id ?? cur;
    });
  };

  // Queue navigation shortcuts. Verdict/submit keys live in ReviewDetail; here we
  // only move the selection. Ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); step(1); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langQueueKey]);

  const reviewQuery = useQuery({
    queryKey: ['review', selectedId],
    queryFn: () => challengesApi.review(selectedId!),
    enabled: !!selectedId,
  });

  return (
    <>
      <PageHead
        crumb="Judgement"
        title="Judge the model"
        lead={
          <>
            Compare the model’s output to the <b style={{ color: 'var(--meta)' }}>gold</b> reference.
            A <b>FAIL</b> on either task means the model was defeated — those segments are the ones
            worth shipping.
          </>
        }
      />

      <div className="pad">
        {langs.length > 1 && (
          <CardSwitch
            label="Viewing language"
            value={effLang}
            onChange={pickLang}
            options={langsByWork.map((c) => {
              const p = pendingFor(c);
              return {
                value: c,
                label: languoidEnglishName(c) ?? c,
                badge: p.total,
                muted: p.total === 0,
                sub: p.total === 0 ? 'all done' : `${p.toJudge} to judge · ${p.toComplete} to complete`,
              };
            })}
          />
        )}

        <div className="l-split">
          <div className="tablewrap" style={{ background: 'var(--panel)' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--hairline)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-faint)', fontWeight: 600 }}>
                Queue <span style={{ color: 'var(--ink-soft)' }}>· {langQueue.length}</span>
                {selectedIndex >= 0 && (
                  <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', textTransform: 'none', letterSpacing: 0, color: 'var(--ink-soft)' }}>
                    {selectedIndex + 1} / {langQueue.length}
                  </span>
                )}
              </div>
              {langQueue.length > 0 && (
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11.5, fontFamily: 'var(--mono)' }}>
                  <span style={{ color: 'var(--warn)' }}>{awaitingVerdict} to judge</span>
                  {toComplete > 0 && <span style={{ color: 'var(--data)' }}>{toComplete} to complete</span>}
                </div>
              )}
            </div>
            {queueQuery.isLoading && (
              <div className="empty" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} className="spin" /> Loading…
              </div>
            )}
            {queueQuery.isError && <div className="empty">Couldn’t load the queue.</div>}
            {queueQuery.isSuccess && langQueue.length === 0 && (
              <div className="empty">Nothing to judge in this language.</div>
            )}
            {langQueue.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '11px 14px',
                  border: 0,
                  borderBottom: '1px solid var(--hairline)',
                  background: c.id === selectedId ? '#fff' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span className="id">{c.segmentId}</span>
                  <span className={`statepill sp-${c.state}`} style={{ marginLeft: 'auto' }}>
                    {STATE_LABEL[c.state] ?? c.state}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 4 }}>{c.domain}</div>
              </button>
            ))}
          </div>

          {!selectedId ? (
            <div className="banner info">Select a challenge to review.</div>
          ) : reviewQuery.isLoading ? (
            <div className="banner info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Loader2 size={14} className="spin" /> Loading the review…
            </div>
          ) : reviewQuery.isError ? (
            <div className="banner bad">
              {reviewQuery.error instanceof ApiError ? reviewQuery.error.message : 'Couldn’t load this review.'}
            </div>
          ) : reviewQuery.data ? (
            <ReviewDetail
              key={selectedId}
              review={reviewQuery.data}
              teamId={teamId}
              position={selectedIndex >= 0 ? selectedIndex + 1 : 0}
              total={langQueue.length}
              onDone={() => step(1)}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

export function ReviewDetail({
  review,
  teamId,
  position = 0,
  total = 0,
  onDone,
}: {
  review: ChallengeReview;
  teamId: string;
  /** 1-based position of this challenge in the queue (0 = unknown). */
  position?: number;
  total?: number;
  /** Advance to the next queue item once a verdict is recorded / completed. */
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const perms = useWebPerms();
  const canApprove = perms.has(Permission.CAN_APPROVE);

  // JUDGED = a single-rater verdict is recorded and the challenge is awaiting
  // approval (which computes the upload route). In that state we don't re-enter
  // verdicts — we approve the recorded one.
  const isApproveStage = review.challenge.state === ChallengeState.JUDGED;
  const isDisputed = review.challenge.state === ChallengeState.DISPUTED;

  const recorded = review.judgements[review.judgements.length - 1];
  const recordedAnyFail = review.judgements.some(
    (j) => j.translationVerdict === 'FAIL' || j.reasoningVerdict === 'FAIL',
  );
  const recordedAnyOther =
    recorded != null && (recorded.translationVerdict === 'OTHER' || recorded.reasoningVerdict === 'OTHER');

  const [translation, setTranslation] = useState<Verdict>('PASS');
  const [reasoning, setReasoning] = useState<Verdict>('PASS');
  // Per-task failure labels — each required only when its own task is FAIL.
  const [translationLabelId, setTranslationLabelId] = useState<number | null>(null);
  const [reasoningLabelId, setReasoningLabelId] = useState<number | null>(null);
  // When either verdict is OTHER the route can't be derived — the judge picks it explicitly
  // and must leave a note. Both are ignored when neither verdict is OTHER.
  const [routeChoice, setRouteChoice] = useState<Route | null>(null);
  const [comment, setComment] = useState('');

  const liveAnyFail = translation === 'FAIL' || reasoning === 'FAIL';
  const liveAnyOther = translation === 'OTHER' || reasoning === 'OTHER';
  // Failure labels are optional — a FAIL with no label is still a complete verdict.
  // On OTHER, both an explicit route and a note are required to submit.
  const otherIncomplete = liveAnyOther && (routeChoice == null || comment.trim() === '');

  // What the verdict row / route show: recorded verdicts at the approve stage,
  // the reviewer's live selection otherwise.
  const showTranslation = isApproveStage ? recorded?.translationVerdict ?? 'PASS' : translation;
  const showReasoning = isApproveStage ? recorded?.reasoningVerdict ?? 'PASS' : reasoning;
  // The route shown in the box: explicit when OTHER, derived from any-FAIL otherwise.
  const shownRoute: Route = isApproveStage
    ? recordedAnyOther
      ? recorded?.routeChoice ?? 'CHALLENGE_SET'
      : recordedAnyFail
        ? 'CHALLENGE_SET'
        : 'NON_CHALLENGE_SET'
    : liveAnyOther
      ? routeChoice ?? 'CHALLENGE_SET'
      : liveAnyFail
        ? 'CHALLENGE_SET'
        : 'NON_CHALLENGE_SET';

  const submit = useMutation({
    mutationFn: () => {
      const idem = newIdempotencyKey();
      const body = {
        translationVerdict: translation,
        reasoningVerdict: reasoning,
        translationFailureLabel: translation === 'FAIL' && translationLabelId != null ? translationLabelId : undefined,
        reasoningFailureLabel: reasoning === 'FAIL' && reasoningLabelId != null ? reasoningLabelId : undefined,
        routeChoice: liveAnyOther && routeChoice != null ? routeChoice : undefined,
        comment: liveAnyOther && comment.trim() ? comment.trim() : undefined,
      };
      return isDisputed
        ? challengesApi.tiebreak(review.challenge.id, idem, body)
        : challengesApi.judge(review.challenge.id, idem, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', teamId] });
      void qc.invalidateQueries({ queryKey: ['review', review.challenge.id] });
      onDone?.(); // auto-advance to the next item in the queue
    },
  });

  const approve = useMutation({
    mutationFn: () => challengesApi.approve(review.challenge.id, newIdempotencyKey()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', teamId] });
      void qc.invalidateQueries({ queryKey: ['review', review.challenge.id] });
      onDone?.(); // auto-advance once the segment is completed
    },
  });

  const taskFor = (t: 'TRANSLATION' | 'REASONING') => review.tasks.find((x) => x.task === t);
  const translationTask = taskFor('TRANSLATION');
  const reasoningTask = taskFor('REASONING');
  const recordedTranslationLabel =
    recorded?.translationFailureLabel != null ? failureLabel(recorded.translationFailureLabel) : undefined;
  const recordedReasoningLabel =
    recorded?.reasoningFailureLabel != null ? failureLabel(recorded.reasoningFailureLabel) : undefined;

  // Keyboard shortcuts for fast judging. 1/2/3 set the translation verdict,
  // 4/5/6 the reasoning verdict, Enter submits (or completes at the approve
  // stage). Queue nav (j/k) lives in the parent. A ref keeps a single window
  // listener bound to the latest state without re-subscribing on every keypress.
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyRef.current = (e: KeyboardEvent) => {
    const el = e.target as HTMLElement | null;
    const tag = el?.tagName ?? '';
    const inField = /^(INPUT|TEXTAREA|SELECT)$/.test(tag);
    // Enter on a focused button belongs to that button, not our submit/complete.
    const enter = e.key === 'Enter' && tag !== 'BUTTON';
    if (isApproveStage) {
      if (enter && !inField && canApprove && review.exportReady && !approve.isPending && !approve.isSuccess) {
        e.preventDefault();
        approve.mutate();
      }
      return;
    }
    if (inField) return; // let the note field receive its own keystrokes
    const tv: Record<string, Verdict | undefined> = { '1': 'PASS', '2': 'FAIL', '3': 'OTHER' };
    const rv: Record<string, Verdict | undefined> = { '4': 'PASS', '5': 'FAIL', '6': 'OTHER' };
    if (tv[e.key]) { e.preventDefault(); setTranslation(tv[e.key]!); }
    else if (rv[e.key]) { e.preventDefault(); setReasoning(rv[e.key]!); }
    else if (enter && !submit.isPending && !submit.isSuccess && !otherIncomplete) {
      e.preventDefault();
      submit.mutate();
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <div>
      <div className="lockbar" style={{ marginBottom: 18 }}>
        <Lock strokeWidth={1.8} />
        <span>
          {review.challenge.segmentId} ·{' '}
          {languoidEnglishName(review.challenge.languoidCode) ?? review.challenge.languoidCode} ·{' '}
          {review.challenge.domain}
        </span>
        {position > 0 && total > 0 && (
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 12, color: '#9094a0' }}>
            {position} / {total}
          </span>
        )}
      </div>

      {isApproveStage && (
        <div className="banner info" style={{ marginBottom: 16 }}>
          A verdict has been recorded — review it and <b>mark it complete</b> to compute the upload route and ship it.
        </div>
      )}

      <ContextCard review={review} />

      {translationTask && (
        <TaskBlock
          title="Translation"
          task={translationTask}
          canViewGold={review.canViewGold}
          verdict={showTranslation}
          onVerdict={setTranslation}
          readOnly={isApproveStage}
        />
      )}
      {reasoningTask && (
        <TaskBlock
          title="Reasoning"
          task={reasoningTask}
          canViewGold={review.canViewGold}
          verdict={showReasoning}
          onVerdict={setReasoning}
          readOnly={isApproveStage}
        />
      )}

      {isApproveStage ? (
        <>
          {recordedAnyFail && (
            <div className="field" style={{ marginTop: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Tag size={13} strokeWidth={2} style={{ color: 'var(--bad)' }} />
                Failure labels <span className="hint">recorded by the reviewer</span>
              </label>
              <div className="jtext">
                {recordedTranslationLabel && (
                  <div>Translation: #{recordedTranslationLabel.id} — {recordedTranslationLabel.label}</div>
                )}
                {recordedReasoningLabel && (
                  <div>Reasoning: #{recordedReasoningLabel.id} — {recordedReasoningLabel.label}</div>
                )}
                {!recordedTranslationLabel && !recordedReasoningLabel && '— none recorded —'}
              </div>
            </div>
          )}
          {recorded?.comment && (
            <div className="field" style={{ marginTop: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <MessagesSquare size={13} strokeWidth={2} /> Reviewer note
              </label>
              <div className="jtext">{recorded.comment}</div>
            </div>
          )}
        </>
      ) : (
        <>
          {translation === 'FAIL' && (
            <LabelSelect title="Translation failure label" value={translationLabelId} onChange={setTranslationLabelId} />
          )}
          {reasoning === 'FAIL' && (
            <LabelSelect title="Reasoning failure label" value={reasoningLabelId} onChange={setReasoningLabelId} />
          )}
          {liveAnyOther && (
            <>
              <div className="field" style={{ marginTop: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <ShieldCheck size={13} strokeWidth={2} />
                  Upload route <span className="hint">required — where does this “in between” segment go?</span>
                </label>
                <div className="routepick">
                  <button
                    type="button"
                    className={'vbtn' + (routeChoice === 'CHALLENGE_SET' ? ' on' : '')}
                    onClick={() => setRouteChoice('CHALLENGE_SET')}
                  >
                    Challenge set
                  </button>
                  <button
                    type="button"
                    className={'vbtn' + (routeChoice === 'NON_CHALLENGE_SET' ? ' on' : '')}
                    onClick={() => setRouteChoice('NON_CHALLENGE_SET')}
                  >
                    Non-challenge set
                  </button>
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <MessagesSquare size={13} strokeWidth={2} />
                  Note <span className="hint">required — explain the “Other” verdict</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Why is the model neither clearly right nor clearly wrong here?"
                />
              </div>
            </>
          )}
        </>
      )}

      <div className="routebox" style={{ marginTop: 16, marginBottom: 16 }}>
        <ShieldCheck size={18} strokeWidth={1.8} />
        <span className="rl">Upload route</span>
        <span className={'rv ' + (shownRoute === 'CHALLENGE_SET' ? 'chal' : 'non')}>
          {shownRoute === 'CHALLENGE_SET' ? 'Challenge set' : 'Non-challenge set'}
        </span>
      </div>

      <div className="stickybar">
        {(submit.isError || approve.isError) && (
          <div className="banner bad" style={{ marginBottom: 12 }}>
            {(submit.error ?? approve.error) instanceof ApiError
              ? (submit.error ?? approve.error as ApiError).message
              : 'Something went wrong.'}
          </div>
        )}

        {isApproveStage ? (
          <div className="toolbar" style={{ gap: 12 }}>
            {approve.isSuccess && <span className="chip confirmed">Completed</span>}
            {!approve.isSuccess && !review.exportReady && (
              <span className="mini" style={{ color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} strokeWidth={2} />
                Tested — can’t complete until every export field + the audio are present.
              </span>
            )}
            {canApprove && review.exportReady && !approve.isSuccess && (
              <span className="kbins">
                <Keyboard size={12} strokeWidth={2} />
                <span className="grp"><span className="kbd">Enter</span> complete</span>
                <span className="grp"><span className="kbd">J</span><span className="kbd">K</span> queue</span>
              </span>
            )}
            <div className="grow" />
            {canApprove ? (
              <button
                className="btn pri"
                disabled={approve.isPending || approve.isSuccess || !review.exportReady}
                title={review.exportReady ? undefined : 'Missing export fields or audio — the segment stays Tested until they’re filled in.'}
                onClick={() => approve.mutate()}
              >
                {approve.isPending ? <Loader2 size={15} className="spin" /> : <BadgeCheck size={15} strokeWidth={1.8} />}
                Mark complete
              </button>
            ) : (
              <span className="mini">Awaiting a reviewer who can complete it.</span>
            )}
          </div>
        ) : (
          <>
            {otherIncomplete && (
              <div className="mini" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--warn)', marginBottom: 8, justifyContent: 'flex-end' }}>
                <AlertTriangle size={13} strokeWidth={2} />
                {routeChoice == null && comment.trim() === ''
                  ? 'Pick an upload route and add a note to submit the “Other” verdict.'
                  : routeChoice == null
                    ? 'Pick an upload route to submit.'
                    : 'Add a note to submit.'}
              </div>
            )}
            <div className="toolbar" style={{ gap: 12 }}>
              <span className="kbins">
                <Keyboard size={12} strokeWidth={2} />
                <span className="grp"><span className="kbd">1</span><span className="kbd">2</span><span className="kbd">3</span> translation</span>
                <span className="grp"><span className="kbd">4</span><span className="kbd">5</span><span className="kbd">6</span> reasoning</span>
                <span className="grp"><span className="kbd">Enter</span> submit</span>
                <span className="grp"><span className="kbd">J</span><span className="kbd">K</span> queue</span>
              </span>
              <div className="grow" />
              {submit.isSuccess && <span className="chip confirmed">Verdict recorded</span>}
              <button
                className="btn pri"
                disabled={submit.isPending || submit.isSuccess || otherIncomplete}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? <Loader2 size={15} className="spin" /> : <Scale size={15} strokeWidth={1.8} />}
                {isDisputed ? 'Submit tiebreak' : 'Submit verdict'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The taxonomy select shown for a task whose verdict is FAIL (per-task label). */
function LabelSelect({
  title,
  value,
  onChange,
}: {
  title: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="field" style={{ marginTop: 14 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Tag size={13} strokeWidth={2} style={{ color: 'var(--bad)' }} />
        {title} <span className="hint">optional — which phenomenon defeated the model?</span>
      </label>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
        <option value="">Select a label…</option>
        {FAILURE_LABELS.map((l) => (
          <option key={l.id} value={l.id}>
            #{l.id} — {l.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function registerTitle(code: string): string {
  const t = decodeRegister(code);
  if (!t) return code;
  return [Connectedness[t.connectedness]?.name, Preparedness[t.preparedness]?.name, SocialDifferential[t.socialDifferential]?.name]
    .filter(Boolean)
    .join(' · ');
}

/** The challenge's audio take statuses — SEGMENT + QUESTION. */
function RecordingsPanel({ recordings }: { recordings: ChallengeReview['recordings'] }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--data-bg)',
        border: '1px solid var(--data-edge)',
        borderRadius: 8,
        padding: '10px 12px',
      }}
    >
      <span className="mini" style={{ color: 'var(--data)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Mic size={13} strokeWidth={2} /> Recordings
      </span>
      <TakeRow label="Segment audio" take={recordings.segment} />
      {recordings.question && <TakeRow label="Question audio" take={recordings.question} />}
    </div>
  );
}

function TakeRow({ label, take }: { label: string; take: ReviewTake }) {
  const fmt = (ms: number | null) => (ms != null ? `${(ms / 1000).toFixed(1)}s` : '');
  const meta: Record<ReviewTake['status'], { text: string; color: string }> = {
    CONFIRMED: { text: `Confirmed${take.durationMs != null ? ` · ${fmt(take.durationMs)}` : ''}`, color: 'var(--good)' },
    DRAFT: { text: 'Draft take (not confirmed)', color: 'var(--warn)' },
    NONE: { text: 'Not recorded', color: 'var(--ink-faint)' },
  };
  const m = meta[take.status];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
      <span style={{ color: 'var(--ink-soft)', minWidth: 110 }}>{label}</span>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color, flex: 'none' }} />
      <span style={{ color: m.color }}>{m.text}</span>
    </div>
  );
}

function ContextCard({ review }: { review: ChallengeReview }) {
  return (
    <div className="zone meta" style={{ marginBottom: 16 }}>
      <div className="zhead">
        <div className="zico">
          <MessagesSquare size={14} strokeWidth={2} />
        </div>
        <div className="ztitle">Context</div>
        <div className="zdesc">{review.challenge.domain}</div>
      </div>
      {review.segmentContext && (
        <p style={{ fontSize: 13, color: 'var(--ink-soft)', marginBottom: 12, lineHeight: 1.5 }}>
          {review.segmentContext}
        </p>
      )}
      <RecordingsPanel recordings={review.recordings} />
      {review.utterances.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {review.utterances.map((u, i) => (
            <div
              key={u.ordinal}
              style={{
                borderTop: i === 0 ? 0 : '1px dashed var(--meta-edge)',
                paddingTop: i === 0 ? 0 : 10,
                marginTop: i === 0 ? 0 : 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                <span className="permchip">{u.speaker}</span>
                <span className="tag" title={registerTitle(u.registerLabel)}>register {u.registerLabel}</span>
                {u.utteranceContext && <span className="mini">{u.utteranceContext}</span>}
              </div>
              {u.transcript && <div className="jtext">{u.transcript}</div>}
              {u.transcriptTransliteration && (
                <div className="jtext" style={{ color: 'var(--ink-faint)', fontStyle: 'italic', marginTop: 3 }}>
                  {u.transcriptTransliteration}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskBlock({
  title,
  task,
  canViewGold,
  verdict,
  onVerdict,
  readOnly = false,
}: {
  title: string;
  task: ReviewTask;
  canViewGold: boolean;
  verdict: Verdict;
  onVerdict: (v: Verdict) => void;
  readOnly?: boolean;
}) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div className="tasklabel">
        {title}
        <span className={'lvl ' + (task.difficulty === 'EXTRA_HARD' ? 'tag xhard' : 'tag hard')}>
          {task.difficulty === 'EXTRA_HARD' ? 'extra hard' : 'hard'}
        </span>
        {canViewGold && task.challengeDescription && (
          <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {task.challengeDescription}
          </span>
        )}
      </div>

      <div className="reviewcols">
        <div className="reviewzone m">
          <div className="rh">
            Gold reference
            <span className="badge">metadata</span>
          </div>
          {canViewGold ? (
            <div className="jtext">{task.gold || <span className="mini">— not provided —</span>}</div>
          ) : (
            <div className="mini">Hidden — you don’t hold can_view_gold.</div>
          )}
        </div>
        <div className="reviewzone d">
          <div className="rh">
            Model output
            <span className="badge">from the model</span>
          </div>
          {task.errorCode ? (
            <div className="jtext" style={{ display: 'flex', alignItems: 'flex-start', gap: 7, color: 'var(--bad)' }}>
              <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: 'none', marginTop: 1 }} />
              <span>{task.errorMessage ?? task.errorCode}</span>
            </div>
          ) : (
            <div className="jtext">{task.modelOutput ?? <span className="mini">— no output —</span>}</div>
          )}
        </div>
      </div>

      <div className="verdictrow" style={{ marginTop: 4 }}>
        <div className="vl">
          Verdict <small>{readOnly ? 'recorded by the reviewer' : 'did the model get it right?'}</small>
        </div>
        <button
          type="button"
          className={'vbtn pass' + (verdict === 'PASS' ? ' on' : '')}
          disabled={readOnly}
          onClick={readOnly ? undefined : () => onVerdict('PASS')}
        >
          PASS
        </button>
        <button
          type="button"
          className={'vbtn fail' + (verdict === 'FAIL' ? ' on' : '')}
          disabled={readOnly}
          onClick={readOnly ? undefined : () => onVerdict('FAIL')}
        >
          FAIL
        </button>
        <button
          type="button"
          className={'vbtn other' + (verdict === 'OTHER' ? ' on' : '')}
          disabled={readOnly}
          onClick={readOnly ? undefined : () => onVerdict('OTHER')}
          title="In between — neither clearly right nor clearly wrong"
        >
          OTHER
        </button>
      </div>
    </section>
  );
}
